import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, streamRuntimeLogs } from '../../lib/api.js'
import { Spinner } from '../../components/ui.jsx'

const MAX_LINEAS = 5000

// Severidad por línea. Reconoce lo que de verdad se usa aquí: pino en JSON, que es lo que emite
// la propia consola, y texto plano con ERROR/WARN. Nada de intentar adivinar más.
const NIVEL_PINO = { 60: 'error', 50: 'error', 40: 'warn', 30: 'info', 20: 'debug', 10: 'debug' }

function analizar(linea) {
  const t = linea.trimStart()
  if (t.startsWith('{')) {
    try {
      const j = JSON.parse(t)
      if (j.level != null) {
        return { nivel: NIVEL_PINO[j.level] ?? 'info', json: j, texto: j.msg ?? linea }
      }
      return { nivel: null, json: j, texto: linea }
    } catch { /* no era JSON */ }
  }
  if (/\b(ERROR|FATAL|Error:|panic:)\b/.test(linea)) return { nivel: 'error', texto: linea }
  if (/\b(WARN|WARNING)\b/.test(linea)) return { nivel: 'warn', texto: linea }
  return { nivel: null, texto: linea }
}

const COLOR = { error: 'var(--danger)', warn: 'var(--warning)', debug: 'var(--fg-3)' }

export function Logs() {
  const { slug } = useParams()
  const [contenedor, setContenedor] = useState('')
  const [tail, setTail] = useState(200)
  const [filtro, setFiltro] = useState('')
  const [pausado, setPausado] = useState(false)
  const [lineas, setLineas] = useState([])
  const [enCola, setEnCola] = useState(0)
  const [estado, setEstado] = useState(null)
  const [aviso, setAviso] = useState(null)
  const [pegado, setPegado] = useState(true)
  const [expandida, setExpandida] = useState(null)

  const cola = useRef([])
  const caja = useRef(null)
  const cerrar = useRef(null)
  const pausadoRef = useRef(pausado)
  pausadoRef.current = pausado

  const cont = useQuery({ queryKey: ['containers', slug], queryFn: () => api.containers(slug) })

  const conectar = useCallback(() => {
    cerrar.current?.()
    setLineas([]); setAviso(null); cola.current = []; setEnCola(0)
    cerrar.current = streamRuntimeLogs(slug, { containerId: contenedor || undefined, tail }, {
      onStatus: setEstado,
      onLine: (l) => {
        if (pausadoRef.current) { cola.current.push(l); setEnCola(cola.current.length); return }
        setLineas((p) => (p.length >= MAX_LINEAS ? [...p.slice(-MAX_LINEAS + 1), l] : [...p, l]))
      },
      // Un hueco silencioso en un log es peor que no tener log: se dice siempre.
      onEnd: (motivo) => setAviso(motivo === 'no_autorizado'
        ? 'Dokploy rechazó la conexión (4003). El flujo se ha cortado.'
        : 'El flujo se ha cerrado. Lo que venga después no se está viendo.'),
      onError: (m) => setAviso(m ?? 'Se cortó la conexión con el servidor de logs.'),
    })
  }, [slug, contenedor, tail])

  useEffect(() => { conectar(); return () => cerrar.current?.() }, [conectar])

  // Al despausar se vuelca lo acumulado de una vez, sin perder nada.
  useEffect(() => {
    if (pausado || !cola.current.length) return
    setLineas((p) => [...p, ...cola.current].slice(-MAX_LINEAS))
    cola.current = []
    setEnCola(0)
  }, [pausado])

  // Anclaje: baja solo mientras estés al final. En cuanto subes a leer, deja de saltar.
  useEffect(() => {
    if (pegado && caja.current) caja.current.scrollTop = caja.current.scrollHeight
  }, [lineas, pegado])

  const visibles = useMemo(() => {
    const f = filtro.trim().toLowerCase()
    return lineas
      .map((l, i) => ({ i, ...analizar(l), cruda: l }))
      .filter((x) => !f || x.cruda.toLowerCase().includes(f))
  }, [lineas, filtro])

  const descargar = () => {
    const blob = new Blob([visibles.map((v) => v.cruda).join('\n')], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${slug}-logs.txt`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">Logs</h1>
          <p className="lede" style={{ marginBottom: 0 }}>
            Salida de la aplicación en marcha. Se conservan los últimos <strong>50 MB</strong> por
            contenedor: lo anterior lo rota Docker y no se puede recuperar.
          </p>
        </div>
        {estado && (
          <span className={`badge ${aviso ? 'error' : 'running'}`}>
            <span className="dot" />{aviso ? 'desconectado' : 'en vivo'}
          </span>
        )}
      </div>

      {cont.isPending && <Spinner />}
      {cont.data?.contenedores?.length === 0 && (
        <div className="alert warn">
          La aplicación no tiene ningún contenedor en marcha. Si acabas de desplegar, dale unos
          segundos.
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 10 }}>
        {cont.data?.contenedores?.length > 1 && (
          <select className="input" value={contenedor} onChange={(e) => setContenedor(e.target.value)}>
            {cont.data.contenedores.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre ?? c.id.slice(0, 12)} · {c.estado}</option>
            ))}
          </select>
        )}
        <select className="input" value={tail} onChange={(e) => setTail(Number(e.target.value))}>
          {[100, 500, 1000, 5000].map((n) => <option key={n} value={n}>últimas {n}</option>)}
        </select>
        <input className="input" placeholder="filtrar…" value={filtro} spellCheck="false"
               style={{ flex: 1, minWidth: 160 }} onChange={(e) => setFiltro(e.target.value)} />
        <button className={`btn sm ${pausado ? 'primary' : 'secondary'}`} onClick={() => setPausado((v) => !v)}>
          {pausado ? `Reanudar${enCola ? ` (${enCola})` : ''}` : 'Pausar'}
        </button>
        <button className="btn sm secondary" onClick={descargar} disabled={!visibles.length}>Descargar</button>
        <button className="btn sm secondary" onClick={conectar}>Reconectar</button>
      </div>

      {aviso && <div className="alert warn" style={{ marginBottom: 10 }}>{aviso}</div>}

      <div className="logs" ref={caja} style={{ maxHeight: '58vh', overflow: 'auto', position: 'relative' }}
           onScroll={(e) => {
             const el = e.currentTarget
             setPegado(el.scrollHeight - el.scrollTop - el.clientHeight < 40)
           }}>
        {visibles.length === 0 && (
          <div className="l" style={{ color: 'var(--fg-3)' }}>
            {lineas.length ? 'Nada coincide con el filtro.' : 'Esperando salida…'}
          </div>
        )}
        {visibles.map((v) => (
          <div className="l" key={v.i} style={{ color: COLOR[v.nivel] }}
               onClick={() => v.json && setExpandida(expandida === v.i ? null : v.i)}>
            {v.json && (
              <span style={{ color: 'var(--fg-3)', cursor: 'pointer' }}>{expandida === v.i ? '▾ ' : '▸ '}</span>
            )}
            {expandida === v.i && v.json ? JSON.stringify(v.json, null, 2) : v.texto}
          </div>
        ))}
      </div>

      {!pegado && (
        <div className="toolbar" style={{ marginTop: 8 }}>
          <button className="btn sm" onClick={() => { setPegado(true); caja.current.scrollTop = caja.current.scrollHeight }}>
            Volver al final
          </button>
          <span className="hint" style={{ marginLeft: 8 }}>
            has subido a leer, así que se ha dejado de seguir
          </span>
        </div>
      )}

      <p className="lede" style={{ marginTop: 12, fontSize: 12 }}>
        Se muestran {visibles.length} de {lineas.length} líneas recibidas
        {lineas.length >= MAX_LINEAS && ' · tope de 5000 en pantalla, las más viejas se descartan'}.
        Las líneas en JSON se despliegan al pulsarlas.
      </p>
    </>
  )
}
